import { useFlags } from "launchdarkly-react-client-sdk";
import * as React from "react";
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import {
  DeepMap,
  FieldError,
  FieldValues,
  UseControllerProps,
  useController,
  useWatch
} from "react-hook-form";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import Autocomplete, {
  AutocompleteRenderGroupParams,
  AutocompleteRenderInputParams
} from "@mui/material/Autocomplete";
import InputAdornment from "@mui/material/InputAdornment";
import Paper, { PaperProps } from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import type { SxProps } from "@mui/material/styles";

import { useDebounce } from "hooks";
import { get } from "lodash";

import { useAppSelector } from "app/hooks";
import {
  categoryGroupOf,
  sortByCategory
} from "components/product-category-filter";
import { ENABLE_CLINIC_STOCK_TAGS } from "constants/feature-flags";
import {
  INVENTORY_PRODUCT_AUTOCOMPLETE_MULTI_CLINIC_PAGE_SIZE,
  INVENTORY_PRODUCT_AUTOCOMPLETE_PAGE_SIZE
} from "constants/inventory-products";
import { selectCurrentClinic } from "features/auth/auth-slice";
import useProductCategories from "hooks/use-product-categories.hook";
import {
  useGetInventoryProductsCountQuery,
  useGetInventoryProductsQuery
} from "services/inventory-products";
import {
  InventoryProduct,
  InventoryProductsFilters
} from "services/types/inventory-products.type";
import { Product } from "services/types/products.type";
import { getProductDisplayType } from "utils/patient-products.util";

import AddInventoryProductForm from "./add-inventory-product-form";
import {
  formatOptionsWithSelectedInventoryProducts,
  sortInventoryProductsByClinic
} from "./inventory-product-field.util";
import InventoryProductGroup, {
  firstGroupsOfEachClinic,
  inventoryGroupKey
} from "./inventory-product-group.component";
import ProductListItem from "./inventory-product-list-item.component";
import useInventoryProductField from "./use-inventory-product-field.hook";

type InventoryProductFieldType<T extends FieldValues> = {
  required?: boolean;
  type: number;
  subtype?: number | number[];
  label: string;
  condition?: number;
  placeholder?: string;
  autofocus?: boolean;
  icon?: ReactNode;
  sx?: SxProps;
  initialSearch?: string;
  clearAfterSelect?: boolean;
  onInventoryProductSelected?: (
    inventoryProduct: InventoryProduct | null | undefined
  ) => void;
  allowInventoryProductAddition?: boolean;
  allowNonStock?: boolean;
  disabled?: boolean;
  filters?: Partial<InventoryProductsFilters>;
  formatOptions?: (inventoryProducts: InventoryProduct[]) => InventoryProduct[];
  product?: Product | null;
  // Show clinic-stock tags read-only in the options (off keeps payload smaller).
  showAssignedTags?: boolean;
  // The scope spans more than one clinic (SPA-6232): band options by clinic
  // (current clinic first), label each row with a clinic pill, and widen the
  // page so other clinics' recent check-ins can't crowd out the current
  // clinic's own stock. The caller still owns `filters.clinic`.
  showClinicGroups?: boolean;
} & UseControllerProps<T>;

const BASE_FILTER: InventoryProductsFilters = {
  search: "",
  display_name: "",
  is_inventory: true
};
// `includeTags` adds the product's own assigned_tags (ENG-1365) only when on,
// and `includeCategory` the product's category id (ENG-2415).
const buildInventoryProductsQuery = (
  includeTags: boolean,
  includeCategory = false
) =>
  `{id,status,loanable,demoable,product{id,name,display_name,cpt_code{id,code},master_product{id,cpt_code{id,code},manufacturer,specification,model,type,subtype,description,is_active},price,supplier_price,component_price,is_active,managed_stock,loss_and_deductible_amount,max_discount,type,has_picture${
    includeCategory ? ",category" : ""
  }},serial_number,available_quantity,quantity,ordered_quantity,clinic{id,name},patient_products{id,patient{id,first_name,last_name},status,assigned_tags}${
    includeTags ? ",assigned_tags" : ""
  },condition,specification,barcode}`;

// Exported so the sibling AddInventoryProductForm can refetch with the same
// projection the picker consumes — otherwise the post-add refetch would fall
// back to `{*}` (which is no longer the RTK service default).
export const INVENTORY_PRODUCTS_QUERY = buildInventoryProductsQuery(false);

const getOptions = (
  options: InventoryProduct[],
  newInventoryProduct?: InventoryProduct
) => {
  if (!newInventoryProduct) return options;
  return [newInventoryProduct, ...options];
};

export default function InventoryProductField<T extends FieldValues>({
  control,
  name,
  required = false,
  label,
  placeholder,
  type,
  subtype,
  condition,
  initialSearch = "",
  icon,
  autofocus = false,
  sx = {},
  clearAfterSelect = false,
  onInventoryProductSelected,
  disabled,
  product = null,
  formatOptions,
  filters = BASE_FILTER,
  allowInventoryProductAddition = false,
  allowNonStock = false,
  showAssignedTags = false,
  showClinicGroups = false
}: InventoryProductFieldType<T>) {
  const flags = useFlags();
  const includeAssignedTags =
    showAssignedTags && Boolean(flags[ENABLE_CLINIC_STOCK_TAGS]);
  const {
    isEnabled: includeCategory,
    categories,
    getCategory
  } = useProductCategories();
  const {
    getIsInventoryProductSelectedInfo,
    rememberSelectedInventoryProduct,
    forgetSelectedInventoryProduct
  } = useInventoryProductField();
  const [openAddForm, setOpenAddForm] = useState<boolean>(false);
  const [search, setSearch] = useState<string>(initialSearch);
  const [options, setOptions] = useState<InventoryProduct[]>([]);
  const [open, setOpen] = useState<boolean>(autofocus);
  const currentClinic = useAppSelector(selectCurrentClinic);
  const { value: dSearch, isDebouncing } = useDebounce(search);
  const {
    field: { onChange, onBlur, ...restField },
    formState: { errors }
  } = useController<T>({
    name,
    control,
    rules: required
      ? {
          required: `${label} is required`
        }
      : undefined
  });
  const errorMessage =
    get(errors, name) &&
    `${(get(errors, name) as DeepMap<FieldValues, FieldError>).message}`;

  const fieldValue = useWatch({ control, name });

  const renderInput = React.useCallback(
    ({ InputProps, ...restParams }: AutocompleteRenderInputParams) => (
      <TextField
        {...restParams}
        InputProps={{
          ...InputProps,
          startAdornment: icon ? (
            <InputAdornment position="start">{icon}</InputAdornment>
          ) : undefined
        }}
        sx={sx}
        InputLabelProps={{ disabled: disabled }}
        required={required}
        error={Boolean(errorMessage)}
        helperText={errorMessage || ""}
        label={label}
        autoFocus={open}
        placeholder={placeholder}
        disabled={disabled}
      />
    ),
    [errorMessage, open, sx, icon, placeholder, label, required, disabled]
  );

  // Shared filter shape — passed to both list and count queries so totalCount
  // stays aligned with the loaded page (same population on both endpoints).
  const countArgs = {
    clinic: currentClinic?.id,
    ...(product
      ? { product: product.id }
      : {
          type,
          subtype
        }),
    condition: condition || undefined,
    ...filters,
    search: dSearch
  };
  const {
    data: inventoryProducts,
    isLoading,
    isSuccess,
    refetch
  } = useGetInventoryProductsQuery({
    ...countArgs,
    limit: showClinicGroups
      ? INVENTORY_PRODUCT_AUTOCOMPLETE_MULTI_CLINIC_PAGE_SIZE
      : INVENTORY_PRODUCT_AUTOCOMPLETE_PAGE_SIZE,
    query: buildInventoryProductsQuery(includeAssignedTags, includeCategory)
  });
  const { data: totalCount = 0 } = useGetInventoryProductsCountQuery(countArgs);

  // Cap signal: server truncation, NOT client filtering. Read raw response
  // length — before `formatOptions` and before the synthetic "Check-in new"
  // option is appended. A future refactor reading from `options.length` would
  // false-negative when formatOptions drops rows that DO exist on the server.
  const isAtCapRef = useRef(false);
  isAtCapRef.current = totalCount > (inventoryProducts?.length ?? 0);

  // Render the "type to search" hint as a footer inside the listbox Paper.
  // Stable identity (useMemo with empty deps + ref) — MUI compares
  // PaperComponent by referential identity. A new function on every isAtCap
  // flip would unmount the Paper subtree (losing scroll, flashing the
  // listbox). See suno-frontend/CLAUDE.md.
  const PaperComponent = useMemo(() => {
    const Component = ({ children, ...rest }: PaperProps) => (
      <Paper {...rest}>
        {children}
        {isAtCapRef.current && (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              color: "text.secondary",
              px: 1.5,
              py: 0.75,
              borderTop: "1px solid",
              borderColor: "divider"
            }}
          >
            Type to search for more inventory products
          </Typography>
        )}
      </Paper>
    );
    Component.displayName = "InventoryProductFieldListboxPaper";
    return Component;
  }, []);

  useEffect(() => {
    if (Boolean(fieldValue)) {
      setSearch((fieldValue as InventoryProduct).product.display_name || "");
    } else {
      setSearch("");
    }
  }, [fieldValue]);

  useEffect(() => {
    const baseOptions = getIsInventoryProductSelectedInfo
      ? formatOptionsWithSelectedInventoryProducts(
          inventoryProducts || [],
          name,
          getIsInventoryProductSelectedInfo
        )
      : inventoryProducts || [];
    const formattedOptions = formatOptions
      ? formatOptions(baseOptions)
      : baseOptions;
    // Sort AFTER formatting so the groups stay contiguous — MUI's groupBy
    // duplicates headers on non-contiguous groups. Category first, then the
    // stable clinic sort, so clinic is the outer band and category the inner.
    const byCategory = includeCategory
      ? sortByCategory(
          formattedOptions,
          categories,
          (option) => option.product?.category
        )
      : formattedOptions;
    setOptions(
      showClinicGroups
        ? sortInventoryProductsByClinic(byCategory, currentClinic?.id)
        : byCategory
    );
  }, [
    inventoryProducts,
    name,
    getIsInventoryProductSelectedInfo,
    formatOptions,
    showClinicGroups,
    currentClinic?.id,
    includeCategory,
    categories
  ]);

  // A list from one category (or a category filter) needs no headings.
  const groupByCategory =
    includeCategory &&
    new Set(
      options.map((option) => getCategory(option.product?.category)?.id ?? null)
    ).size > 1;
  const groupKeyOf = (option: InventoryProduct) =>
    // The synthetic "Check-in new ..." row belongs to no group.
    option.id === -1
      ? ""
      : inventoryGroupKey({
          clinic: showClinicGroups ? (option.clinic?.name ?? "") : null,
          category: groupByCategory
            ? categoryGroupOf(
                { category: option.product?.category },
                getCategory
              )
            : null
        });
  const clinicBandGroups = firstGroupsOfEachClinic(options.map(groupKeyOf));

  const handleChange = (
    data:
      | NonNullable<string | InventoryProduct>
      | (string | InventoryProduct)[]
      | null
  ) => {
    if ((data as any)?.id === -1) {
      setOpenAddForm(true);
      return;
    }
    onChange(data);
    if (rememberSelectedInventoryProduct && !data && fieldValue) {
      forgetSelectedInventoryProduct(name);
    } else if (forgetSelectedInventoryProduct && data) {
      rememberSelectedInventoryProduct(data as any, name);
    }
    if (Boolean(data)) {
      setSearch(
        clearAfterSelect
          ? ""
          : (data as InventoryProduct)?.product?.display_name || ""
      );
      if (onInventoryProductSelected) {
        onInventoryProductSelected(data as InventoryProduct);
      }
    } else {
      if (onInventoryProductSelected) {
        onInventoryProductSelected(data as null);
      }
    }
    onBlur();
  };

  if (!isSuccess) {
    return <></>;
  }

  return (
    <>
      <Autocomplete
        disablePortal
        loading={isLoading || isDebouncing}
        fullWidth
        disabled={disabled}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        open={open}
        options={getOptions(
          options,
          allowInventoryProductAddition
            ? ({
                id: -1,
                product: {
                  display_name: `Check-in new ${
                    product
                      ? product.display_name
                      : getProductDisplayType({
                          type,
                          subtype:
                            typeof subtype === "number"
                              ? subtype.toString()
                              : undefined
                        })
                  }`
                }
              } as any as InventoryProduct)
            : undefined
        )}
        getOptionLabel={(option) =>
          typeof option === "string"
            ? option
            : option.product?.display_name || ""
        }
        renderOption={(props: object, option: InventoryProduct) => (
          <ProductListItem
            props={props}
            option={option}
            isNew={option.id === -1}
            showAssignedTags={includeAssignedTags}
            showClinic={showClinicGroups}
            currentClinicId={currentClinic?.id}
            key={`${option.id}-${option?.serial_number}`}
          />
        )}
        groupBy={showClinicGroups || groupByCategory ? groupKeyOf : undefined}
        renderGroup={(params: AutocompleteRenderGroupParams) => (
          <InventoryProductGroup
            {...params}
            currentClinicName={currentClinic?.name}
            clinicBandGroups={clinicBandGroups}
          />
        )}
        isOptionEqualToValue={(
          option: InventoryProduct,
          value: InventoryProduct
        ) => option.id === value.id}
        inputValue={search}
        onInputChange={(event, newInputValue, reason) => {
          if (Boolean(event)) {
            setSearch(reason === "input" ? newInputValue : "");
          }
        }}
        filterOptions={(x) => x}
        PaperComponent={PaperComponent}
        popupIcon={<ArrowDropDownIcon />}
        renderInput={renderInput}
        onChange={(_, data) => {
          handleChange(data);
        }}
        onBlur={onBlur}
        {...restField}
      />
      <AddInventoryProductForm
        isOpen={openAddForm}
        onCancel={() => setOpenAddForm(false)}
        onSave={async (inventoryProduct: InventoryProduct) => {
          refetch();
          handleChange(inventoryProduct);
          setOpenAddForm(false);
        }}
        newInventoryProduct={{
          type: type.toString(),
          subtype: typeof subtype === "number" ? subtype.toString() : "",
          product: product || undefined,
          condition: condition || undefined
        }}
        allowNonStock={allowNonStock}
      />
    </>
  );
}
